import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { endpoints } from "@/lib/api";
import {
  Check, Clock, Sunrise, Moon, Circle, Flower2, ArrowUpRight, Flame,
  Plus, Trash2, Sparkles, Pencil, Share2, Brain, HeartPulse, Feather, Compass, BookOpen,
  Heart, Sun, Leaf, User, Mountain, ArrowRight, Star, Users,
} from "lucide-react";
import N8GuideCard from "@/components/N8GuideCard";
import N8Emblem from "@/components/N8Emblem";
import ProGate from "@/components/ProGate";
import GoalProgressRibbon from "@/components/GoalProgressRibbon";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import ReminderPill from "@/components/ReminderPill";
import PersonalizedRitualEngine from "@/components/PersonalizedRitualEngine";
import ShareRitualDialog from "@/components/ShareRitualDialog";
import TimeOfDayBadge from "@/components/TimeOfDayBadge";
import SkinEssentialsRitual from "@/components/SkinEssentialsRitual";
import BodyCompanions from "@/components/BodyCompanions";
import BodyFaceEssentials from "@/components/BodyFaceEssentials";
import AffirmationsLibrary from "@/components/AffirmationsLibrary";
import DailyIntentionSelector from "@/components/DailyIntentionSelector";
import MeditationTab from "@/components/MeditationTab";
import { QuietReadCard, QuietReadReader } from "@/components/StoicArticle";
import { QUIET_READS } from "@/lib/quietReads";
import SoulPathCard from "@/components/soul/SoulPathCard";
import { SOUL_PATHS } from "@/pages/soul/soulContent";
import SharedRitualsFeed from "@/components/SharedRitualsFeed";
import ThemedWeekBanner from "@/components/ThemedWeekBanner";
import RitualsHero from "@/components/RitualsHero";
import DynamicIntegrationArea from "@/components/DynamicIntegrationArea";
import ClosingMoment from "@/components/ClosingMoment";
import { N8 } from "@/components/BrandMark";
import { celebrateRitualCompleted } from "@/components/SoulRitualToast";

const MILESTONE_DAYS = [7, 14, 30];
const MILESTONE_KEY = "rituals.streak.milestone.seen";
const MILESTONE_LABEL = { 7: "A week unbroken", 14: "Two weeks unbroken", 30: "A month unbroken" };

/** Read the largest milestone the member has already been shown, so we
 *  never surface the same ribbon twice — quieter is kinder. */
function readSeenMilestone() {
  try { return parseInt(localStorage.getItem(MILESTONE_KEY) || "0", 10) || 0; }
  catch { return 0; }
}
function markMilestoneSeen(m) {
  try { localStorage.setItem(MILESTONE_KEY, String(m)); } catch { /* noop */ }
}

const timeIcon = (t) => (t === "morning" ? <Sunrise size={14} /> : t === "evening" ? <Moon size={14} /> : <Circle size={14} />);

/**
 * Rituals — two-tab experience.
 *
 *   1. "My Rituals"  — everything the member owns: seeded starter rituals,
 *                       Rituals adopted from a wellness practice, and self-authored rituals with
 *                       an inline "Create a ritual" dialog.
 *   2. "N8 Guide"    — the Pro coach: N8GuideCard (personalized daily
 *                       priorities + 4-part recommendations, both grounded
 *                       in the member's Tracking history and Profile goals)
 *                       + the PersonalizedRitualEngine's daily plan.
 */
export default function Rituals() {
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const soulSeed = searchParams.get("seed");
  const soulRequest = searchParams.get("soul");
  const [pillar, setPillar] = useState(() => {
    try {
      const v = sessionStorage.getItem("rituals-pillar") || "mind";
      // Legacy migrations: "spirit" → "soul"; "shared" → "body" (Shared now
      // lives as a Body sub-tab).
      if (v === "spirit") return "soul";
      if (v === "shared") return "body";
      return v;
    } catch { return "mind"; }
  });
  // Deep-link handoff from Journal: /rituals?soul=work&seed=<journal_id>
  useEffect(() => {
    if (soulRequest === "work") {
      setPillar("soul");
      try { sessionStorage.setItem("rituals-soul-tab", "work"); } catch { /* noop */ }
    }
  }, [soulRequest]);
  useEffect(() => {
    try { sessionStorage.setItem("rituals-pillar", pillar); } catch { /* noop */ }
  }, [pillar]);
  const [tab, setTab] = useState(() => {
    try {
      // Legacy: if the user had "shared" saved elsewhere, route them
      // to the new Body → Shared sub-tab home on first load.
      if (sessionStorage.getItem("rituals-pillar") === "shared") return "shared";
      const v = sessionStorage.getItem("rituals-body-tab") || "essentials";
      // The N8 Guide sub-tab has moved to Home. Any legacy saved value
      // gently migrates to Essentials.
      return v === "n8" ? "essentials" : v;
    } catch { return "essentials"; }
  });
  useEffect(() => {
    try { sessionStorage.setItem("rituals-body-tab", tab); } catch { /* noop */ }
  }, [tab]);

  const [mindTab, setMindTab] = useState(() => {
    try { return sessionStorage.getItem("rituals-mind-tab") || "affirmations"; } catch { return "affirmations"; }
  });
  useEffect(() => {
    try { sessionStorage.setItem("rituals-mind-tab", mindTab); } catch { /* noop */ }
  }, [mindTab]);
  const [stoicOpen, setStoicOpen] = useState(false);
  const [openReadSlug, setOpenReadSlug] = useState(null);
  const openArticle = openReadSlug ? QUIET_READS.find((a) => a.slug === openReadSlug) : null;

  const [rituals, setRituals] = useState([]);
  const [done, setDone] = useState(new Set());
  const [filter, setFilter] = useState("all");
  const [streak, setStreak] = useState(0);
  const [wellnessCode, setWellnessCode] = useState("");
  // Whether to overlay a one-time celebration ribbon on the streak chip.
  const [milestone, setMilestone] = useState(null);

  const load = async () => {
    const [r, c, s] = await Promise.all([endpoints.rituals(), endpoints.completionsToday(), endpoints.streak()]);
    setRituals(r.data);
    setDone(new Set(c.data.ritual_ids));
    setStreak(s.data.streak);
  };
  useEffect(() => { load(); }, []);

  // Watch streak for a just-crossed milestone (works whether streak was set
  // via `load` on mount or via `toggle` when the member checks a ritual).
  useEffect(() => {
    const seen = readSeenMilestone();
    const crossed = MILESTONE_DAYS.filter((m) => streak >= m && m > seen);
    if (crossed.length) {
      const top = crossed[crossed.length - 1];
      setMilestone(top);
      // Fire the larger, longer celebration for streak milestones.
      try {
        window.dispatchEvent(new CustomEvent("soul-ritual-completed", {
          detail: { milestone: top, label: MILESTONE_LABEL[top] || `${top} days` },
        }));
      } catch { /* noop */ }
    }
  }, [streak]);

  const dismissMilestone = () => {
    if (milestone) markMilestoneSeen(milestone);
    setMilestone(null);
  };

  const toggle = async (id) => {
    const { data } = await endpoints.completeRitual(id);
    setDone((prev) => {
      const n = new Set(prev);
      if (data.completed) {
        n.add(id);
        toast.success("Honored.");
        celebrateRitualCompleted();
      } else {
        n.delete(id);
        toast("Set aside for now.");
      }
      return n;
    });
    const s = await endpoints.streak();
    setStreak(s.data.streak);
  };

  const removeRitual = async (id) => {
    try {
      await endpoints.deleteRitual(id);
      toast.success("Released.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not release.");
    }
  };

  const filtered = rituals.filter((r) => filter === "all" || r.time_of_day === filter);
  const filters = [
    { key: "all", label: "All" },
    { key: "morning", label: "Morning" },
    { key: "anytime", label: "Anytime" },
    { key: "evening", label: "Evening" },
  ];

  return (
    <div className="space-y-8" data-testid="rituals-page">
      <RitualsHero />

      {/* Ambient session chips — streak + honored today. Kept subtle so
          they support the hero without competing with it. */}
      <div className="flex items-center justify-between gap-3 flex-wrap px-1" data-testid="rituals-session-strip">
        <div className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--muted-foreground))] flex items-center gap-3">
          <span>Becoming</span>
          <span className="opacity-40" aria-hidden>·</span>
          <TimeOfDayBadge testid="rituals-time-of-day" />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-sm px-3 py-1.5 rounded-full bg-[hsl(var(--sand))]" data-testid="rituals-done-today">
            <span className="text-[hsl(var(--muted-foreground))]">Honored today</span>{" "}
            <span className="font-display text-lg align-middle">{done.size}</span> / {rituals.length}
          </div>
          <div
            className="relative text-sm px-3 py-1.5 rounded-full bg-[hsl(var(--sand))] inline-flex items-center gap-1.5"
            data-testid="rituals-streak"
            title="Consecutive days you have honored at least one ritual"
          >
            <Flame size={12} className="text-[hsl(var(--copper))]" />
            <span className="text-[hsl(var(--muted-foreground))] text-[10px] uppercase tracking-[0.2em]">Unbroken</span>
            <span className="font-display text-lg align-middle">{streak}</span>
            <span className="text-[hsl(var(--muted-foreground))] text-xs">days</span>
            {milestone && (
              <button
                type="button"
                onClick={dismissMilestone}
                data-testid="rituals-streak-milestone"
                aria-label={`Milestone: ${MILESTONE_LABEL[milestone]}. Tap to dismiss.`}
                className="absolute -top-2 -right-1 px-2 py-0.5 rounded-full bg-[hsl(var(--copper))] text-[hsl(var(--paper))] text-[9px] uppercase tracking-[0.18em] shadow-md whitespace-nowrap tab-link hover:opacity-90"
                style={{ animation: "reveal 500ms ease-out both" }}
              >
                {MILESTONE_LABEL[milestone] || `${milestone} days`}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Top-level pillar tabs — Mind · Body · Soul · Shared.
          The static N8ONE emblem sits above the pillars so the primary
          Rituals experience — choosing a path — carries the brand mark
          rather than the hero banner. */}
      <div className="flex items-center justify-center" data-testid="rituals-emblem-wrap">
        <N8Emblem
          mode="full"
          size={54}
          ariaLabel="N8ONE emblem"
          testid="rituals-emblem"
        />
      </div>
      <div
        className="flex items-center justify-center gap-3 flex-wrap pt-4"
        data-testid="rituals-pillar-tabs"
      >
        <PillarTab
          active={pillar === "mind"}
          onClick={() => setPillar("mind")}
          testid="rituals-pillar-mind"
          label="Mind"
          hint="Clarity & Conscious Thought"
          Icon={Brain}
        />
        <PillarTab
          active={pillar === "body"}
          onClick={() => setPillar("body")}
          testid="rituals-pillar-body"
          label="Body"
          hint="Vitality & Intentional Care"
          Icon={HeartPulse}
        />
        <PillarTab
          active={pillar === "soul"}
          onClick={() => setPillar("soul")}
          testid="rituals-pillar-soul"
          label="Soul"
          hint="Connection & Inner Growth"
          Icon={Feather}
        />
      </div>

      {pillar === "mind" && (
        <section className="space-y-8" data-testid="rituals-mind-pillar">
          <div className="flex items-center justify-center gap-2.5 flex-wrap" data-testid="rituals-mind-tabs">
            <TabButton
              active={mindTab === "affirmations"}
              onClick={() => setMindTab("affirmations")}
              testid="rituals-mind-tab-affirmations"
              label="Daily Affirmations"
              hint="Words that rewire you"
              Icon={Feather}
            />
            <TabButton
              active={mindTab === "intentions"}
              onClick={() => setMindTab("intentions")}
              testid="rituals-mind-tab-intentions"
              label="Daily Intentions"
              hint="One intention. Set the tone."
              Icon={Compass}
              accent
            />
            <TabButton
              active={mindTab === "meditation"}
              onClick={() => setMindTab("meditation")}
              testid="rituals-mind-tab-meditation"
              label="Meditation"
              hint="Quiet the mind. Meet the heart."
              Icon={Heart}
            />
            <TabButton
              active={mindTab === "read"}
              onClick={() => setMindTab("read")}
              testid="rituals-mind-tab-read"
              label="A Quiet Read"
              hint="Calm words for the mind"
              Icon={BookOpen}
            />
          </div>

          {mindTab === "affirmations" && <AffirmationsLibrary />}
          {mindTab === "intentions" && <DailyIntentionSelector />}
          {mindTab === "meditation" && <MeditationTab />}
              {mindTab === "read" && (() => {
                const featured = QUIET_READS.filter((a) => a.featured);
                const standard = QUIET_READS.filter((a) => !a.featured);
                return (
                  <div className="space-y-10" data-testid="rituals-mind-quiet-read">
                    <div>
                      <div className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--terracotta))] inline-flex items-center gap-2">
                        <BookOpen size={12} /> A Quiet Read
                      </div>
                      <h2 className="font-display text-3xl md:text-4xl mt-3 tracking-tight italic leading-tight">
                        A moment to become.
                      </h2>
                      <p className="mt-3 max-w-2xl text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">
                        Short reads for reflection, perspective, and the person you&rsquo;re becoming.
                      </p>
                    </div>

                    {featured.length > 0 && (
                      <div data-testid="foundational-reads-shelf">
                        <div className="flex items-baseline justify-between gap-3 flex-wrap mb-4">
                          <div>
                            <div className="text-[11px] uppercase tracking-[0.28em] text-[hsl(var(--copper))]">
                              Foundational Reads
                            </div>
                            <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))] italic max-w-md">
                              The essays that anchor the philosophy behind Becoming.
                            </p>
                          </div>
                        </div>
                        <div className="grid gap-4 md:grid-cols-2" data-testid="foundational-reads-grid">
                          {featured.map((a) => (
                            <QuietReadCard
                              key={a.slug}
                              article={a}
                              onOpen={() => setOpenReadSlug(a.slug)}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    <div>
                      <div className="flex items-baseline justify-between gap-3 flex-wrap mb-4">
                        <div className="text-[11px] uppercase tracking-[0.28em] text-[hsl(var(--terracotta))]">
                          More Quiet Reads
                        </div>
                      </div>
                      <div className="grid gap-4 md:grid-cols-2" data-testid="quiet-reads-grid">
                        {standard.map((a) => (
                          <QuietReadCard
                            key={a.slug}
                            article={a}
                            onOpen={() => setOpenReadSlug(a.slug)}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })()}

          <QuietReadReader
            open={!!openArticle}
            article={openArticle}
            onClose={() => setOpenReadSlug(null)}
            onSeeded={() => setOpenReadSlug(null)}
          />
        </section>
      )}

      {pillar === "body" && (
        <section className="reveal reveal-1 space-y-8" data-testid="rituals-body-pillar">
          {/* Body sub-tabs */}
          <div className="flex items-center justify-center gap-2 flex-wrap" data-testid="rituals-body-tabs">
            <TabButton
              active={tab === "essentials"}
              onClick={() => setTab("essentials")}
              testid="rituals-body-tab-essentials"
              label="Essentials"
              hint="Skin, movement, sleep"
              Icon={HeartPulse}
            />
            <TabButton
              active={tab === "mine"}
              onClick={() => setTab("mine")}
              testid="rituals-body-tab-mine"
              label="Your Practice"
              hint="Rituals you tend, in your own hand"
              Icon={Circle}
            />
            <TabButton
              active={tab === "shared"}
              onClick={() => setTab("shared")}
              testid="rituals-body-tab-shared"
              label="Shared"
              hint="From wellness professionals"
              Icon={Users}
            />
          </div>

          {tab === "essentials" && (
            <BodyFaceEssentials />
          )}

          {tab === "mine" && (
            <MyRitualsView
              rituals={rituals}
              filtered={filtered}
              filter={filter}
              setFilter={setFilter}
              filters={filters}
              done={done}
              toggle={toggle}
              onCreated={load}
              onRemove={removeRitual}
              wellnessCode={wellnessCode}
              setWellnessCode={setWellnessCode}
              nav={nav}
              reload={load}
            />
          )}

          {tab === "shared" && (
            <SharedRitualsFeed />
          )}
        </section>
      )}

      {pillar === "soul" && (
        <SoulPillarPanel
          initialSubTab={soulRequest === "work" ? "work" : null}
          seedJournalId={soulSeed || null}
          onSeedConsumed={() => {
            // Clean the URL once we've handed the seed off so refreshes
            // don't re-trigger a session.
            const next = new URLSearchParams(searchParams);
            next.delete("soul");
            next.delete("seed");
            setSearchParams(next, { replace: true });
          }}
        />
      )}

      {/* Legacy — the "shared" pillar has moved into Body → Shared. */}

      {/* Dynamic integration — reflects the active Mind sub-mode. Kept in
          the flow (not a footer) so the ritual and the ripple sit close. */}
      <DynamicIntegrationArea mode={pillar === "mind" ? mindTab : "affirmations"} />

      {/* Closing punctuation for the page. Same on every pillar. */}
      <ClosingMoment />
    </div>
  );
}

function PillarTab({ active, onClick, testid, label, hint, Icon }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      aria-pressed={active}
      title={hint}
      className={`inline-flex items-center justify-center gap-2.5 w-[10.5rem] sm:w-[12rem] py-3 rounded-full border transition-all duration-300 tab-link ${
        active
          ? "bg-[hsl(var(--copper))] text-[hsl(var(--paper))] border-transparent shadow-[0_10px_24px_-14px_hsl(var(--copper)/0.7)]"
          : "bg-[hsl(var(--paper))] text-[hsl(var(--fg))]/85 border-[hsl(var(--line))] hover:border-[hsl(var(--copper-light))] hover:shadow-[0_6px_18px_-14px_hsl(var(--copper)/0.4)] hover:-translate-y-[1px]"
      }`}
    >
      <Icon size={15} strokeWidth={active ? 2 : 1.75} />
      <span className="font-display text-lg tracking-wide">{label}</span>
    </button>
  );
}


function SoulPillarPanel({ initialSubTab, seedJournalId, onSeedConsumed }) {
  // Soul Compass now lives only in Becoming → Discover. The Soul pillar here
  // opens directly into Soul Work.
  const seedConsumedRef = useRef(false);
  useEffect(() => {
    if (!seedConsumedRef.current && (seedJournalId || initialSubTab === "work")) {
      seedConsumedRef.current = true;
      const t = setTimeout(() => { onSeedConsumed?.(); }, 600);
      return () => clearTimeout(t);
    }
  }, [seedJournalId, initialSubTab, onSeedConsumed]);

  return (
    <section
      className="reveal reveal-1 relative overflow-hidden rounded-md border hairline"
      style={{
        background:
          "radial-gradient(ellipse at 50% 30%, hsl(42,55%,97%) 0%, hsl(38,42%,94%) 45%, hsl(32,30%,88%) 100%)",
        color: "hsl(24, 28%, 22%)",
      }}
      data-testid="rituals-soul-pillar"
    >
      <div className="relative z-10 p-6 md:p-10">
        <div data-testid="rituals-soul-work-panel">
          <SoulWorkInvitation />
        </div>
      </div>
    </section>
  );
}

/**
 * SoulWorkInvitation — a quiet, editorial doorway into the new Soul
 * experience (which now lives at /soul). Replaces the retired in-place
 * SoulWork module while keeping the "Soul Work" sub-tab discoverable.
 */
function SoulWorkInvitation() {
  return (
    <div className="space-y-8" data-testid="soul-work-invitation">
      {/* Editorial intro — quieter than a full hero because the Rituals
          Soul tab already carries the top banner. */}
      <div className="max-w-2xl">
        <div className="text-[11px] uppercase tracking-[0.28em] text-[hsl(var(--copper))]">
          Choose Your Path
        </div>
        <h2 className="font-display text-3xl md:text-4xl mt-3 tracking-tight italic">
          Where would you like to begin?
        </h2>
        <p className="mt-4 text-[15px] leading-relaxed text-[hsl(var(--fg))]/80">
          There is no right place to start. Choose the path that meets you where you are today.
        </p>
      </div>

      {/* The two pathways — Regulation & Presence and Inner Exploration —
          are now rendered directly here so members don't have to enter a
          separate Soul Work screen to see them. */}
      <div className="grid gap-8 lg:grid-cols-2">
        {SOUL_PATHS.map((p) => (
          <SoulPathCard
            key={p.slug}
            eyebrow={p.eyebrow}
            title={p.title}
            tagline={p.tagline}
            body={p.body}
            contains={p.contains}
            image={p.image}
            to={`/soul/${p.slug}`}
            testid={`soul-path-${p.slug}`}
          />
        ))}
      </div>
    </div>
  );
}
function TabButton({ active, onClick, testid, label, hint, Icon, accent = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      data-testid={testid}
      title={hint}
      className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full transition-all duration-300 tab-link border ${
        active
          ? "bg-[hsl(var(--copper))] text-[hsl(var(--paper))] border-transparent shadow-[0_10px_24px_-14px_hsl(var(--copper)/0.7)]"
          : "bg-[hsl(var(--paper))] text-[hsl(var(--fg))]/80 border-[hsl(var(--line))] hover:border-[hsl(var(--copper-light))]"
      }`}
    >
      <Icon size={13} strokeWidth={active ? 2 : 1.75} />
      <span className="text-[13px] font-medium tracking-wide">{label}</span>
    </button>
  );
}

function MyRitualsView({
  rituals, filtered, filter, setFilter, filters,
  done, toggle, onCreated, onRemove, wellnessCode, setWellnessCode, nav, reload,
}) {
  const [editing, setEditing] = useState(null); // ritual object or null
  const [sharing, setSharing] = useState(null); // ritual object or null
  return (
    <div className="space-y-8 reveal reveal-2">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-2xl">Your practice</h2>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
            Author your own, receive one from a wellness practice, or begin with a shared vessel.
          </p>
        </div>
        <CreateRitualDialog onSaved={onCreated} />
      </div>

      <section className="p-6 border hairline rounded-md bg-[hsl(var(--paper))]" data-testid="wellness-code-panel">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))] inline-flex items-center gap-2">
              <Flower2 size={12} className="text-[hsl(var(--terracotta))]" /> A ritual received
            </div>
            <p className="font-display text-2xl mt-2 italic">A gift from a wellness practice. Enter the code they shared.</p>
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const c = wellnessCode.trim().toUpperCase();
              if (!c) return;
              nav(`/r/${c}`);
            }}
            className="flex gap-2 items-center"
          >
            <Input
              value={wellnessCode}
              onChange={(e) => setWellnessCode(e.target.value.toUpperCase())}
              placeholder="e.g. FC1B5545"
              data-testid="wellness-code-input"
              maxLength={12}
              className="bg-transparent tracking-widest font-mono uppercase w-44"
            />
            <Button
              type="submit"
              disabled={!wellnessCode.trim()}
              data-testid="wellness-code-open"
              className="rounded-full bg-[hsl(var(--terracotta))] hover:bg-[hsl(var(--terracotta))]/90 text-[hsl(var(--fg))]"
            >
              Receive
            </Button>
          </form>
        </div>
        <div className="mt-3 text-xs text-[hsl(var(--muted-foreground))]">
          Or if you hold a wellness practice,{" "}
          <a href="/wellness" className="underline underline-offset-4 hover:text-[hsl(var(--terracotta))]" data-testid="wellness-creator-link">
            share a ritual with your clients <ArrowUpRight size={11} className="inline" />
          </a>
        </div>
      </section>

      <div className="flex gap-2 flex-wrap">
        {filters.map((f) => (
          <button
            key={f.key}
            data-testid={`filter-${f.key}`}
            onClick={() => setFilter(f.key)}
            className={`px-4 py-2 rounded-full text-sm tab-link border hairline ${
              filter === f.key ? "bg-[hsl(var(--fg))] text-[hsl(var(--paper))]" : "hover:bg-[hsl(var(--sand))]"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div
          className="p-10 text-center rounded-md bg-[hsl(var(--paper))] border hairline"
          data-testid="rituals-empty"
        >
          <p className="font-display text-xl italic">
            A clean altar. Your first ritual waits to be named.
          </p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">{ritualsCards()}</div>
      )}

      {/* Edit dialog is a controlled version of CreateRitualDialog that
          opens whenever `editing` is set. */}
      <CreateRitualDialog
        editing={editing}
        externalOpen={!!editing}
        onExternalClose={() => setEditing(null)}
        onSaved={async () => { setEditing(null); await onCreated?.(); }}
      />

      {/* Share dialog — open when a ritual has been chosen for sharing. */}
      <ShareRitualDialog
        ritual={sharing}
        open={!!sharing}
        onClose={() => setSharing(null)}
        onShared={() => setSharing(null)}
      />
    </div>
  );

  function ritualsCards() {
    return filtered.map((r) => {
      const isDone = done.has(r.id);
      return (
        <article
          key={r.id}
          data-testid={`ritual-${r.slug}`}
          className={`p-6 border rounded-md bg-[hsl(var(--paper))] transition-colors ${
            r.is_custom ? "border-[hsl(var(--terracotta))]/50 shadow-[inset_3px_0_0_hsl(var(--terracotta))]" : "hairline"
          } ${isDone ? "opacity-60" : ""}`}
        >
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))] flex-wrap">
                {timeIcon(r.time_of_day)} {r.time_of_day} · <Clock size={12} /> {r.duration_min}m · {r.category}
                {r.is_custom && r.source_spa && (
                  <span className="px-2 py-0.5 rounded-full bg-[hsl(var(--terracotta))]/20 text-[hsl(var(--fg))] normal-case tracking-normal text-[10px] inline-flex items-center gap-1" data-testid={`ritual-from-spa-${r.slug}`}>
                    <Flower2 size={10} /> received from {r.source_spa}
                  </span>
                )}
                {r.is_custom && !r.source_spa && (
                  <span className="px-2 py-0.5 rounded-full bg-[hsl(var(--clay))]/20 text-[hsl(var(--fg))] normal-case tracking-normal text-[10px]">
                    cultivated by you
                  </span>
                )}
              </div>
              <h3 className="font-display text-2xl mt-2">{r.title}</h3>
              {r.description && (
                <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">{r.description}</p>
              )}
              {r.steps?.length > 0 && (
                <ol className="mt-4 space-y-1 text-sm">
                  {r.steps.map((s, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="text-[hsl(var(--muted-foreground))] w-4">{i + 1}.</span>
                      <span>{s}</span>
                    </li>
                  ))}
                </ol>
              )}
              {r.is_custom && (
                <div className="mt-4">
                  <ReminderPill ritual={r} onChange={reload} />
                </div>
              )}
            </div>
            <div className="shrink-0 flex flex-col items-end gap-2">
              <button
                onClick={() => toggle(r.id)}
                data-testid={`toggle-${r.slug}`}
                aria-pressed={isDone}
                className={`w-11 h-11 rounded-full border hairline grid place-items-center tab-link ${
                  isDone ? "bg-[hsl(var(--moss))] border-transparent text-[hsl(var(--paper))]" : "hover:bg-[hsl(var(--sand))]"
                }`}
              >
                <Check size={18} />
              </button>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setSharing(r)}
                  data-testid={`share-${r.slug}`}
                  aria-label={`Offer ritual "${r.title}" to the circle`}
                  title="Offer this to the circle"
                  className="p-1.5 rounded-full text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--terracotta))] tab-link"
                >
                  <Share2 size={14} />
                </button>
                {r.is_custom && !r.source_spa && (
                  <>
                    <button
                      onClick={() => setEditing(r)}
                      data-testid={`edit-${r.slug}`}
                      aria-label={`Refine ritual "${r.title}"`}
                      title="Refine this ritual"
                      className="p-1.5 rounded-full text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--clay))] tab-link"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      onClick={() => onRemove(r.id)}
                      data-testid={`delete-${r.slug}`}
                      aria-label={`Release ritual "${r.title}"`}
                      title="Release this ritual"
                      className="p-1.5 rounded-full text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--terracotta))] tab-link"
                    >
                      <Trash2 size={14} />
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </article>
      );
    });
  }
}

function CreateRitualDialog({ onSaved, editing = null, externalOpen = false, onExternalClose }) {
  // In "create" mode the dialog is uncontrolled and rendered with its own
  // trigger button. In "edit" mode the caller passes an `editing` ritual and
  // controls open state via `externalOpen`. We reuse one component so the
  // form UI stays in sync between the two flows.
  const isEditing = !!editing;
  const [open, setOpen] = useState(false);
  const controlledOpen = isEditing ? externalOpen : open;
  const setControlledOpen = (v) => {
    if (isEditing) {
      if (!v) onExternalClose?.();
    } else {
      setOpen(v);
    }
  };

  const [title, setTitle] = useState("");
  const [tod, setTod] = useState("morning");
  const [duration, setDuration] = useState(10);
  const [category, setCategory] = useState("personal");
  const [description, setDescription] = useState("");
  const [stepsRaw, setStepsRaw] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setTitle(""); setTod("morning"); setDuration(10);
    setCategory("personal"); setDescription(""); setStepsRaw("");
  };

  // When editing switches on (or the underlying ritual changes), hydrate the
  // form from the ritual. On close-in-edit-mode, we don't reset — the parent
  // will unmount / remount by toggling `editing` to null.
  useEffect(() => {
    if (isEditing && editing) {
      setTitle(editing.title || "");
      setTod(editing.time_of_day || "morning");
      setDuration(editing.duration_min || 10);
      setCategory(editing.category || "personal");
      setDescription(editing.description || "");
      setStepsRaw((editing.steps || []).join("\n"));
    }
  }, [isEditing, editing]);

  const submit = async (e) => {
    e?.preventDefault?.();
    const t = title.trim();
    if (!t) return;
    setBusy(true);
    try {
      const steps = stepsRaw.split("\n").map((s) => s.trim()).filter(Boolean);
      const payload = {
        title: t,
        time_of_day: tod,
        duration_min: Math.max(1, Math.min(240, Number(duration) || 10)),
        category: category.trim() || "personal",
        description: description.trim(),
        steps,
      };
      if (isEditing) {
        await endpoints.updateRitual(editing.id, payload);
        toast.success("Refined.");
      } else {
        await endpoints.createRitual(payload);
        toast.success("Ritual welcomed in.");
        reset();
      }
      setControlledOpen(false);
      await onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save.");
    }
    setBusy(false);
  };

  return (
    <Dialog
      open={controlledOpen}
      onOpenChange={(v) => { setControlledOpen(v); if (!v && !isEditing) reset(); }}
    >
      {!isEditing && (
        <DialogTrigger asChild>
          <Button
            data-testid="rituals-create-btn"
            className="rounded-full bg-[hsl(var(--clay))] text-[hsl(var(--paper))] hover:bg-[hsl(var(--clay))]/90 inline-flex items-center gap-2"
          >
            <Plus size={14} /> Begin a new ritual
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="max-w-lg" data-testid={isEditing ? "rituals-edit-dialog" : "rituals-create-dialog"}>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {isEditing ? "Refine your ritual" : "Begin a new ritual"}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? "Adjust with care — title, steps, timing. Small tweaks compound."
              : "Name something small you will return to. A minute or an hour — the return is what matters."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">Title</label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={120}
              placeholder="Evening tea + one page"
              data-testid={isEditing ? "rituals-edit-title" : "rituals-create-title"}
              className="mt-1"
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">Time</label>
              <select
                value={tod}
                onChange={(e) => setTod(e.target.value)}
                data-testid={isEditing ? "rituals-edit-time" : "rituals-create-time"}
                className="mt-1 w-full bg-transparent border hairline rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[hsl(var(--clay))]/50"
              >
                <option value="morning">morning</option>
                <option value="anytime">anytime</option>
                <option value="evening">evening</option>
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">Duration</label>
              <div className="relative mt-1">
                <Input
                  type="number"
                  min={1}
                  max={240}
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  data-testid={isEditing ? "rituals-edit-duration" : "rituals-create-duration"}
                  className="pr-10"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-[hsl(var(--muted-foreground))]">min</span>
              </div>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">Category</label>
              <Input
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                maxLength={32}
                placeholder="skin, sleep, movement…"
                data-testid={isEditing ? "rituals-edit-category" : "rituals-create-category"}
                className="mt-1"
              />
            </div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">A sentence about it</label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              maxLength={600}
              placeholder="What this ritual means to you, in your own words."
              data-testid={isEditing ? "rituals-edit-description" : "rituals-create-description"}
              className="mt-1"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">Steps (one per line, optional)</label>
            <Textarea
              value={stepsRaw}
              onChange={(e) => setStepsRaw(e.target.value)}
              rows={4}
              placeholder={"Warm water\nBrew tea\nOne page from the book on the nightstand"}
              data-testid={isEditing ? "rituals-edit-steps" : "rituals-create-steps"}
              className="mt-1 font-mono text-sm"
            />
            <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1">
              Up to twelve steps. Trim what does not need saying.
            </div>
          </div>
          <DialogFooter className="pt-1">
            <Button
              type="button"
              variant="outline"
              onClick={() => setControlledOpen(false)}
              className="rounded-full"
              data-testid={isEditing ? "rituals-edit-cancel" : "rituals-create-cancel"}
            >
              Not now
            </Button>
            <Button
              type="submit"
              disabled={!title.trim() || busy}
              data-testid={isEditing ? "rituals-edit-submit" : "rituals-create-submit"}
              className="rounded-full bg-[hsl(var(--clay))] text-[hsl(var(--paper))] hover:bg-[hsl(var(--clay))]/90"
            >
              {busy ? "Cultivating…" : (isEditing ? "Preserve the changes" : "Welcome it in")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
